// noinspection SpellCheckingInspection
import AddBoxIcon from "@mui/icons-material/AddBox";
import { Theme } from "@mui/material";
import Button from "@mui/material/Button";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import { styled } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { OrderDirection } from "_shared/_api/ordering/order-direction";
import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";
import { NotAuthorized } from "_shared/_errors/_views/not-authorized";
import { RootState, useAttorneyHubDispatch } from "_shared/_stores/redux-store";
import { MRTNoMemo } from "_shared/_views/tables/table";
import Guid from "_shared/guids/guid";
import {
  createMRTColumnHelper,
  MRT_ShowHideColumnsButton as ShowHideColumnsButton,
  MRT_ToggleDensePaddingButton as ToggleDensePaddingButton,
  MRT_ToggleFiltersButton as ToggleFiltersButton,
  MRT_ToggleFullScreenButton as ToggleFullScreenButton,
  MRT_ToggleGlobalFilterButton as ToggleGlobalFilterButton,
  type MRT_TableInstance,
  type MRT_ColumnFiltersState as ColumnFiltersState,
  type MRT_TableOptions,
  type MRT_PaginationState as PaginationState,
  type MRT_Row,
} from "material-react-table";
import { enqueueSnackbar } from "notistack";
import { getSessionPermissions, populateSessionPermissions } from "permissions/_stores/permissions-redux-slice";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams, useSearchParams } from "react-router";
import { useSession } from "users/__session/session-context";
import WorkAgreementQuery, {
  WorkAgreementFilterParameters,
  WorkAgreementOrderParameters,
} from "work/work-agreements/_api/queries/work-agreement-query";
import {
  getLastSuccessfulWorkAgreementQueryByInterface,
  getWorkAgreementQueryByInterface,
  getWorkAgreementsByQuery,
  isWorkAgreementLoadIssue,
  updateWorkAgreementInterfaceQuery,
  WorkAgreementLoadIssue
} from "work/work-agreements/_store/work-agreement-redux-slice";
import WorkAgreementToolbar from "work/work-agreements/_views/_toolbars/work-agreement-toolbar";
import { PageTab } from "work/work-agreements/_views/work-agreements-page-tabs";
import WorkAgreement from "work/work-agreements/work-agreement";
import { WorkAgreementOrderField } from "../_api/queries/_fields/work-agreement-order-field";
import { useGetWorkAgreementsQuery } from "work/work-agreements/_api/work-agreement-api";
import { skipToken } from "@reduxjs/toolkit/query";

const ToolbarButtonsContainer = styled("div")(({ theme }) => ({
  [theme.breakpoints.down("md")]: {
    flexDirection: "row",
    justifyContent: "space-between",
    width: "100%",
  },
  display: "flex",
  alignItems: "end",
  flexDirection: "column",
  gap: theme.spacing(1),
}));
const ToolbarButtons = styled("div")(() => ({
  display: "flex",
  alignItems: "end",
  flexWrap: "nowrap",
}));
const RowTitle = styled(Typography)(() => ({
  fontSize: "1.1em",
  fontWeight: "600",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
}));
const NewButton = styled(IconButton)(() => ({
  padding: 0,
}));

const columnHelper = createMRTColumnHelper<WorkAgreement | WorkAgreementLoadIssue>();

export default function WorkAgreementsPage() {
  const { pageTab: pageTabParam } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useAttorneyHubDispatch();
  const session = useSession();
  const sessionPermissions = useSelector(getSessionPermissions);
  const sessionPermissionsLoading = useSelector((state: RootState) => state.permissions.session.loading);
  const sessionPermissionsError = useSelector((state: RootState) => state.permissions.session.error);

  const isMediumDisplaySize = useMediaQuery((theme: Theme) => theme.breakpoints.down("md"));

  const pageTab = pageTabParam as PageTab | undefined;
  const effectivePageTab = pageTab ?? PageTab.Active;
  const interfaceName = `workAgreementsPage:${effectivePageTab}`;

  const workAgreementsQuery = useSelector(getWorkAgreementQueryByInterface(interfaceName));

  const paginatedWorkAgreementsJson = useSelector(getWorkAgreementsByQuery(workAgreementsQuery));
  const paginatedWorkAgreements = useMemo(() => {
    if (!paginatedWorkAgreementsJson) return;
    const deserializedData: ((WorkAgreement | WorkAgreementLoadIssue) & { id: Guid })[] = [];
    for (const workAgreementJson of paginatedWorkAgreementsJson.data) {
      try {
        if (isWorkAgreementLoadIssue(workAgreementJson)) {
          deserializedData.push(workAgreementJson);
        } else {
          const workAgreement = WorkAgreement.fromJSON(workAgreementJson);
          if (workAgreement?.id) {
            deserializedData.push(workAgreement as WorkAgreement & { id: Guid });
          } else {
            console.error("WorkAgreement does not have an id", workAgreementJson);
            const issue = { id: Guid.EMPTY, issue: "Error loading WorkAgreement." };
            deserializedData.push(issue);
          }
        }
      } catch (error) {
        const id = "id" in workAgreementJson ? (Guid.fromJSON(workAgreementJson.id) ?? Guid.EMPTY) : Guid.EMPTY;
        deserializedData.push({ id, issue: "Error loading WorkAgreement." });
        console.error("Error deserializing WorkAgreement JSON:", error);
      }
    }
    return deserializedData;
  }, [paginatedWorkAgreementsJson]);

  const workAgreementsAPIQuery = useGetWorkAgreementsQuery(
    workAgreementsQuery ? { query: workAgreementsQuery, interfaceName } : skipToken
  );

  // TODO: make sure to namespace by tab to avoid showing incorrect counts/data when switching tabs
  const lastSuccessfulQueryResponseJson = useSelector(getLastSuccessfulWorkAgreementQueryByInterface(interfaceName));

  const lastSuccessfulQueryResponse = useMemo(() => {
    if (!lastSuccessfulQueryResponseJson) return;
    const deserializedData: ((WorkAgreement | WorkAgreementLoadIssue) & { id: Guid })[] = [];
    for (const item of lastSuccessfulQueryResponseJson.data) {
      try {
        if (isWorkAgreementLoadIssue(item)) {
          deserializedData.push(item);
        } else {
          const workAgreement = WorkAgreement.fromJSON(item);
          if (workAgreement?.id) {
            deserializedData.push(workAgreement as WorkAgreement & { id: Guid });
          } else {
            console.error("Work Agreement does not have an id", item);
            const issue = { id: Guid.EMPTY, issue: "Error loading Work Agreement." };
            deserializedData.push(issue);
          }
        }
      } catch (error) {
        const id = "id" in item ? (Guid.fromJSON(item.id) ?? Guid.EMPTY) : Guid.EMPTY;
        deserializedData.push({ id, issue: "Error loading Work Agreement." });
        console.error("Error deserializing Work Agreement JSON:", error);
      }
    }
    return deserializedData;
  }, [lastSuccessfulQueryResponseJson]);

  const tableWorkAgreements = paginatedWorkAgreements ?? lastSuccessfulQueryResponse;

  const [globalFilter, setGlobalFilter] = useState("");
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [sorting, setSorting] = useState<Array<{ id: string; desc: boolean }>>([]);
  const [pagination, setPagination] = useState<PaginationState>(PaginationParameters.DEFAULT);

  useEffect(() => {
    if (!sessionPermissions && !sessionPermissionsError && !sessionPermissionsLoading) {
      dispatch(populateSessionPermissions({ session }));
    }
  }, [dispatch, session, sessionPermissions, sessionPermissionsError, sessionPermissionsLoading]);

  useEffect(() => {
    if (!session.user?.legalEntityId) return;

    const filterParameters = WorkAgreementFilterParameters.fromSearchParams({
      searchParams,
      overrides: {
        isArchived: pageTab === PageTab.Archived
      }
    });
    const orderParameters = WorkAgreementOrderParameters.fromSearchParams(searchParams);

    const newQuery = WorkAgreementQuery.create({
      filterParameters,
      orderParameters,
      paginationParameters: PaginationParameters.fromSearchParams(searchParams),
    });
    if (workAgreementsQuery && newQuery.isEqualTo(workAgreementsQuery)) return;
    dispatch(
      updateWorkAgreementInterfaceQuery({
        interfaceName: interfaceName,
        query: newQuery,
      }),
    );
  }, [pageTab, session.user?.legalEntityId.value, session, searchParams, workAgreementsQuery, dispatch, interfaceName]);

  useEffect(() => {
    const newSearchParams = new URLSearchParams();
    if (globalFilter) {
      newSearchParams.set("query", globalFilter);
    }

    const nameFilter = columnFilters.find((filter) => filter.id === "name")?.value as string | undefined;
    if (nameFilter) {
      newSearchParams.set("name", nameFilter);
    }

    const descriptionFilter = columnFilters.find((filter) => filter.id === "description")?.value as string | undefined;
    if (descriptionFilter) {
      newSearchParams.set("description", descriptionFilter);
    }

    const orderingField = sorting.find((sort) =>
      Object.values(WorkAgreementOrderField)
        .map((field) => field.toLowerCase())
        .includes(sort.id.toLowerCase()),
    );

    if (orderingField) {
      newSearchParams.set("orderBy", orderingField.id);
      newSearchParams.set("orderDir", orderingField.desc ? OrderDirection.Descending : OrderDirection.Ascending);
    }

    const paginationPageIndex = pagination.pageIndex ?? 0;
    newSearchParams.set("page", (paginationPageIndex + 1).toString());
    const paginationPageSize = pagination.pageSize ?? 10;
    newSearchParams.set("pageSize", paginationPageSize.toString());

    setSearchParams(newSearchParams);
  }, [globalFilter, columnFilters, sorting, pagination, setSearchParams]);

  useEffect(() => {
    if (workAgreementsAPIQuery.isError) {
      enqueueSnackbar("Error loading work agreement table", { variant: "error" });
    }
  }, [workAgreementsAPIQuery.isError]);

  const renderNameColumn = useCallback(
    (workAgreement: WorkAgreement | WorkAgreementLoadIssue) => {
      if (workAgreementsAPIQuery.isLoading) return;
      if (isWorkAgreementLoadIssue(workAgreement) || !workAgreement.name) return <Icon>remove</Icon>;
      return <RowTitle>{workAgreement.name.value}</RowTitle>;
    },
    [workAgreementsAPIQuery.isLoading]
  );

  const renderDescriptionColumn = useCallback(
    (workAgreement: WorkAgreement | WorkAgreementLoadIssue) => {
      if (workAgreementsAPIQuery.isLoading) return;
      if (isWorkAgreementLoadIssue(workAgreement) || !workAgreement.description) return <Icon>remove</Icon>;
      return workAgreement.description.value;
    },
    [workAgreementsAPIQuery.isLoading]
  );

  const columns = useMemo(() => {
    const returnedColumns = [
      columnHelper.display({
        header: "Name",
        id: "name",
        Cell: ({ row }) => renderNameColumn(row.original)
      }),
      columnHelper.display({
        header: "Description",
        id: "description",
        enableSorting: false,
        Cell: ({ row }) => renderDescriptionColumn(row.original)
      }),
      columnHelper.display({
        header: "Hire Date",
        id: "hireDate",
        enableSorting: false,
        Cell: HireDateCell
      }),
      columnHelper.display({
        header: "Start Date",
        id: "start",
        Cell: StartDateCell
      }),
      columnHelper.display({
        header: "End Date",
        id: "end",
        Cell: EndDateCell
      }),
    ];
    if (pageTab === PageTab.Archived) {
      returnedColumns.push(
        columnHelper.display({
          header: "Archive Date",
          id: "archiveDate",
          Cell: ArchiveDateCell
        }),
      );
    }
    return returnedColumns;
  }, [pageTab, renderDescriptionColumn, renderNameColumn]);

  const openWorkAgreement = useCallback(
    (workAgreement: WorkAgreement | WorkAgreementLoadIssue) => {
      if (isWorkAgreementLoadIssue(workAgreement)) {
        console.warn("Cannot open work agreement with loading issues.");
        return;
      }
      if (!workAgreement.id) {
        console.error("Work Agreement ID is required");
        return;
      }
      navigate(`/work/${pageTab}/view/${workAgreement.id}`);
    },
    [navigate, pageTab],
  );

  const renderTopToolbarCustomActions = useCallback(
    ({ table }: { table: MRT_TableInstance<WorkAgreement | WorkAgreementLoadIssue> }) => {
      return <WorkAgreementToolbar table={table} activeTab={pageTab ?? PageTab.Active} />;
    },
    [pageTab],
  );

  const renderToolbarInternalActions = useCallback(
    (props: { table: MRT_TableInstance<WorkAgreement | WorkAgreementLoadIssue> }) => (
      <>
        {!isMediumDisplaySize && (
          <ToolbarButtonsContainer>
            <ToolbarButtons>
              <ToggleGlobalFilterButton table={props.table} />
              <ToggleFiltersButton table={props.table} />
              <ShowHideColumnsButton table={props.table} />
              <ToggleDensePaddingButton table={props.table} />
              <ToggleFullScreenButton table={props.table} />
            </ToolbarButtons>
            <Button
              startIcon={<AddBoxIcon />}
              disabled={session.user?.isSuperUser}
              onClick={() => navigate("/proposals/drafts/create")}
            >
              Create Proposal
            </Button>
          </ToolbarButtonsContainer>
        )}
        {isMediumDisplaySize && (
          <NewButton color="primary" onClick={() => navigate("/proposals/drafts/create")}>
            <AddBoxIcon fontSize="large" />
          </NewButton>
        )}
      </>
    ),
    [isMediumDisplaySize, session.user?.isSuperUser, navigate],
  );

  const isPreparingWorkAgreementsQuery =
    !sessionPermissions || (Boolean(session.user?.legalEntityId) && !workAgreementsQuery);
  const isTableLoading = isPreparingWorkAgreementsQuery || workAgreementsAPIQuery.isFetching;
  const showTableSkeletons = !tableWorkAgreements && isTableLoading;
  const skeletonWorkAgreements = useMemo(
    () =>
      Array.from({ length: pagination.pageSize }, (_, index) => ({
        id: Guid.generate(),
        issue: `Loading work agreement ${index}`,
      })),
    [pagination.pageSize],
  );
  const tableData = useMemo(
    () => (showTableSkeletons ? skeletonWorkAgreements : (tableWorkAgreements ?? [])),
    [showTableSkeletons, skeletonWorkAgreements, tableWorkAgreements],
  );
  const tableRowCount = showTableSkeletons ? pagination.pageSize : (tableWorkAgreements?.length ?? 0);

  const muiTableBodyRowProps = useCallback(
    ({ row }: { row: MRT_Row<WorkAgreement | WorkAgreementLoadIssue> }) => ({
      onClick: showTableSkeletons ? undefined : () => openWorkAgreement(row.original),
      sx: {
        backgroundColor: !showTableSkeletons && isWorkAgreementLoadIssue(row.original)
          ? "rgba(255, 100, 100, 0.1)" // light red for error
          : "inherit", // default color
        cursor: showTableSkeletons || isWorkAgreementLoadIssue(row.original) ? "default" : "pointer",
      },
    }),
    [openWorkAgreement, showTableSkeletons],
  );

  const muiToolbarAlertBannerProps = useMemo(() => {
    if (workAgreementsAPIQuery.isError) {
      return { color: "error" as const, children: "Error loading data" };
    }
  }, [workAgreementsAPIQuery.isError]);

  const tableOptions = useMemo<MRT_TableOptions<WorkAgreement | WorkAgreementLoadIssue>>(() => {
    return {
      columns,
      data: tableData,
      enableRowSelection: false,
      enableTableHead: true,
      muiTableBodyRowProps,
      renderToolbarInternalActions,
      renderTopToolbarCustomActions,
      getRowId: (row) => row.id?.value ?? Guid.generate().value,
      manualFiltering: true,
      enableStickyHeader: true,
      enableStickyFooter: true,
      manualPagination: true,
      manualSorting: true,
      muiTableContainerProps: {
        sx: {
          flex: 1,
        },
      },
      muiTablePaperProps: {
        sx: {
          height: "100%",
          display: "flex",
          flexDirection: "column",
        },
      },
      muiToolbarAlertBannerProps,
      rowCount: tableRowCount,
      pageCount: Math.ceil(tableRowCount / pagination.pageSize),
      enablePagination: true,
      onColumnFiltersChange: setColumnFilters,
      onGlobalFilterChange: setGlobalFilter,
      onPaginationChange: setPagination,
      onSortingChange: setSorting,
      state: {
        columnFilters,
        globalFilter,
        isLoading: isTableLoading,
        pagination,
        showAlertBanner: Boolean(workAgreementsAPIQuery.isError),
        showProgressBars: workAgreementsAPIQuery.isFetching,
        sorting,
        showSkeletons: showTableSkeletons,
      },
    };
  }, [
    columns,
    tableData,
    tableRowCount,
    muiTableBodyRowProps,
    renderToolbarInternalActions,
    renderTopToolbarCustomActions,
    muiToolbarAlertBannerProps,
    pagination,
    columnFilters,
    globalFilter,
    isTableLoading,
    workAgreementsAPIQuery.isError,
    workAgreementsAPIQuery.isFetching,
    showTableSkeletons,
    sorting
  ]);

  if (!sessionPermissions) return <MRTNoMemo<WorkAgreement | WorkAgreementLoadIssue> options={tableOptions} />;
  if (!sessionPermissions.includesAccessToWork) return <NotAuthorized />;

  return <MRTNoMemo<WorkAgreement | WorkAgreementLoadIssue> options={tableOptions} />;
}

function StartDateCell({ row, table }:
  {
    row: { original: WorkAgreement | WorkAgreementLoadIssue },
    table: MRT_TableInstance<WorkAgreement | WorkAgreementLoadIssue>
  }) {
  if (table.getState().isLoading) return;
  const value = !isWorkAgreementLoadIssue(row.original) && row.original.startDate;
  return value ? value.format("MM/DD/YYYY") : <Icon>remove</Icon>;
}

function EndDateCell({ row, table }: 
  { 
    row: { original: WorkAgreement | WorkAgreementLoadIssue },
    table: MRT_TableInstance<WorkAgreement | WorkAgreementLoadIssue>
  }) {
  if (table.getState().isLoading) return;
  const value = !isWorkAgreementLoadIssue(row.original) && row.original.endDate;
  return value ? value.format("MM/DD/YYYY") : <Icon>remove</Icon>;
}

function HireDateCell({ row, table }: 
  { 
    row: { original: WorkAgreement | WorkAgreementLoadIssue },
    table: MRT_TableInstance<WorkAgreement | WorkAgreementLoadIssue>
  }) {
  if (table.getState().isLoading) return;
  const value = !isWorkAgreementLoadIssue(row.original) && row.original.hireDate;
  return value ? value.format("MM/DD/YYYY") : <Icon>remove</Icon>;
}

function ArchiveDateCell({ row, table }: 
  { 
    row: { original: WorkAgreement | WorkAgreementLoadIssue },
    table: MRT_TableInstance<WorkAgreement | WorkAgreementLoadIssue>
  }) {
  if (table.getState().isLoading) return;
  const value = !isWorkAgreementLoadIssue(row.original) && row.original.archiveDate;
  return value ? value.format("MM/DD/YYYY") : <Icon>remove</Icon>;
}
